<template>
  <v-card class="py-2">
    <v-icon v-on:click.stop="$emit('close')" large class="closeIcon mt-3 mx-2"
      >mdi-close</v-icon
    >
    <h2
      weight="200"
      class="mt-6 d-flex font-weight-regular justify-center align-center"
    >
      Modifier le fichier "{{
        nameOrigin ? truncateString(nameOrigin) : "..."
      }}"
    </h2>
    <v-divider light color="#EF7D00" class="mt-4 mb-6 mx-4"></v-divider>
    <v-form
      ref="form"
      v-model="isFormValid"
      class="d-flex flex-column px-10 py-4"
    >
      <div>
        <v-row>
          <v-text-field
            v-model="file.name"
            label="Nom du fichier"
            prepend-icon="mdi-file-document"
            required
            :rules="name_rule"
          ></v-text-field>
        </v-row>
        <v-row class="d-flex align-center">
          <v-col>Visibilité : </v-col>
        </v-row>
        <div
          v-for="field in visibilityFields"
          :key="field.key"
          class="ml-4 mb-2"
        >
          <v-autocomplete
            v-model="file.visibility[field.key]"
            :items="field.items"
            :label="field.label"
            :prepend-icon="field.icon"
            :rules="field.rules"
            multiple
            dense
          >
            <template v-slot:selection="{ item }">
              <v-chip
                close
                class="mx-1 my-1"
                @click:close="removeVisibilityItem(field.key, item)"
              >
                {{ item }}
              </v-chip>
            </template>
          </v-autocomplete>
        </div>
        <v-row class="d-flex align-center">
          <v-col
            >Déplacer
            <span v-if="newParent"
              >dans le dossier {{ newParent.name }}</span
            ></v-col
          >
        </v-row>
        <TreeForMoving :itemId="file._id" @change="changeNewParent" />
      </div>
    </v-form>
    <div class="d-flex justify-center mb-4">
      <v-btn @click="deleteFile()" large elevation="2" color="error">
        <v-icon>mdi-delete</v-icon> Supprimer le fichier
      </v-btn>
    </div>

    <ValidateErrorButtons
      @successClicked="validate"
      @errorClicked="closeDialog"
      :disabled="!$refs.form || !$refs.form.validate()"
    >
      <template v-slot:error>ANNULER</template>
      <template v-slot:success>VALIDER</template>
    </ValidateErrorButtons>
  </v-card>
</template>

<script>
import ValidateErrorButtons from "../../components/ValidateErrorButtons.vue";
import TreeForMoving from "./TreeForMoving.vue";

const API_TO_UI_ROLES = { teacher: "Tuteur", student: "Tutoré" };
const UI_TO_API_ROLES = { Tuteur: "teacher", Tutoré: "student" };

export default {
  name: "DialogModifyFile",
  components: {
    ValidateErrorButtons,
    TreeForMoving,
  },
  props: {
    file: {
      type: Object,
      required: true,
      default: () => ({
        name: "",
        children: [],
        visibility: { roles: [], schools: [], years: [] },
      }),
    },
    parent: {
      type: Object,
      required: true,
      default: () => ({
        name: "",
        children: [],
        visibility: { roles: [], schools: [], years: [] },
      }),
    },
  },
  data: () => ({
    isFormValid: false,
    nameOrigin: null,

    newParent: null,
  }),
  computed: {
    name_rule: function () {
      return [
        !!this.file.name || "Le nom ne peut pas être vide",
        !this.parent.children.files
          .filter((i) => i._id !== this.file._id)
          .map((i) => i.name.toLowerCase())
          .includes(this.file.name.toLowerCase()) ||
          "ce nom est déjà utilisé par un autre fichier",
      ];
    },
    visibilityFields() {
      return [
        {
          key: "roles",
          label: "Rôles",
          icon: "mdi-book",
          items: ["Tuteur", "Tutoré"],
          rules: this.createVisibilityRules("roles", "rôle"),
        },
        {
          key: "schools",
          label: "Écoles",
          icon: "mdi-school",
          items: ["ESILV", "EMLV", "IIM"],
          rules: this.createVisibilityRules("schools", "école"),
        },
        {
          key: "years",
          label: "Années",
          icon: "mdi-cake",
          items: ["A1", "A2", "A3", "A4", "A5"],
          rules: this.createVisibilityRules("years", "année"),
        },
      ];
    },
  },
  methods: {
    changeNewParent(item) {
      this.newParent = item;
    },

    truncateString(str, maxLength = 27) {
      if (str.length <= maxLength) return str;
      return str.slice(0, maxLength - 1) + "…";
    },
    createVisibilityRules(key, singularName) {
      if (!this.parent?.visibility) return [true];

      let parentVisibilityItems = [];
      if (key === "roles") {
        parentVisibilityItems =
          this.parent.visibility.roles?.map((r) => API_TO_UI_ROLES[r] || r) ||
          [];
      } else {
        parentVisibilityItems = this.parent.visibility[key] || [];
      }

      return [
        (v) => {
          const errors = (v || []).filter(
            (item) => !parentVisibilityItems.includes(item)
          );
          if (errors.length === 0) return true;
          const plural = errors.length > 1;
          return `Le${plural ? "s" : ""} ${singularName}${
            plural ? "s" : ""
          } "${errors.join(", ")}" n'est pas permis par le dossier parent.`;
        },
      ];
    },
    removeVisibilityItem(key, item) {
      const index = this.file.visibility[key].indexOf(item);
      if (index >= 0) this.file.visibility[key].splice(index, 1);
    },
    validate: function () {
      this.$refs.form.validate();
      if (!this.isFormValid) return;
      let payload = {
        name: this.file.name,
        parent: this.newParent ? this.newParent._id : this.file.parent,
        visibility: {
          roles: this.file.visibility.roles.map((r) => UI_TO_API_ROLES[r]),
          schools: this.file.visibility.schools,
          years: this.file.visibility.years,
        },
      };
      this.$store
        .dispatch("leodata/modifyFile", {
          fileId: this.file._id,
          payload,
        })
        .then(() => {
          this.$emit("close");
        });
    },
    closeDialog() {
      this.$emit("close");
    },
    initializeForm() {
      this.nameOrigin = this.file.name;
      this.file.visibility.roles =
        this.file.visibility.roles.map((r) => API_TO_UI_ROLES[r]) || [];
      this.newParent = null;
    },
    deleteFile() {
      this.$swal
        .fire({
          title: `Êtes-vous sûr de vouloir supprimer ce fichier ?`,
          icon: "warning",
          html: `Cette action sera irréversible.`,
          confirmButtonText: "Supprimer",
          confirmButtonColor: "#d33",
          showCancelButton: true,
          cancelButtonText: "Annuler",
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.$store.dispatch("leodata/deleteFile", this.file._id);
            this.$emit("close");
          }
        });
    },
  },
  created() {
    this.initializeForm();
  },
  watch: {
    file(newValue) {
      if (newValue) {
        this.initializeForm();
      }
    },
  },
};
</script>
<style scoped>
.v-sheet {
  position: relative;
  color: #8f8f8f;
}
.v-icon,
.v-input {
  color: #8f8f8f;
}
.closeIcon {
  position: absolute;
  right: 0;
  top: 0;
}

.no-truncate .v-file-input__text {
  color: red;
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
}
</style>
